import { msalInstance } from './msal.instance';
import {
  exchangeMicrosoftToken,
  getAccountData,
} from '../utils/api/account_api';
import {
  clearSessionStorage,
  clearToken,
  saveToken,
  saveRefreshToken,
  setConsulenteId,
  setGruppoId,
  setSuperconsulente,
} from '../utils/storage';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiError } from '../utils/api/api';

const LOGIN_SCOPES = ['openid', 'profile', 'email'];

export const loginMicrosoft = () => {
  return msalInstance.loginRedirect({
    scopes: LOGIN_SCOPES,
    prompt: 'select_account', // force account selection on each login
  });
};

export const logoutMicrosoft = () => {
  const account = msalInstance.getActiveAccount();

  return msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: window.location.origin,
    mainWindowRedirectUri: window.location.origin,
  });
};

export const getActiveAccount = () => {
  return msalInstance.getActiveAccount();
};

export const completeMicrosoftLogin = async () => {
  const activeAccount = msalInstance.getActiveAccount();

  if (!activeAccount) {
    return {
      ok: false,
      reason: 'NO_ACTIVE_ACCOUNT',
    };
  }

  // Acquire token silently for the defined scopes
  // (this will use cached tokens or refresh if needed)
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account: activeAccount,
      scopes: LOGIN_SCOPES,
    });

    if (!tokenResponse?.idToken) {
      return {
        ok: false,
        reason: 'MISSING_ID_TOKEN',
      };
    }

    const result = await exchangeMicrosoftToken(tokenResponse.idToken);

    if (!result?.token) {
      return {
        ok: false,
        reason: 'BACKEND_JWT_MISSING',
      };
    }

    saveToken(result.token);
    saveRefreshToken(result.refreshToken);
    try {
      const mod = await import('store/Store');
      const storeState = mod.default.getState();
      storeState.setToken(result.token);
      if (
        result.refreshToken &&
        typeof storeState.setRefreshToken === 'function'
      ) {
        storeState.setRefreshToken(result.refreshToken);
      }
    } catch (e) {
      console.warn('[completeMicrosoftLogin] shared store non disponibile:', e);
    }

    // carica dati account
    const accountData = await getAccountData();

    const ruoli = Array.isArray(accountData?.ruoli) ? accountData.ruoli : [];

    const onlyMobileRole =
      ruoli.length === 1 && ruoli.includes('ROLE_RESPONSABILESICUREZZA');

    if (onlyMobileRole) {
      clearToken();
      clearSessionStorage();
      try {
        const mod = await import('store/Store');
        mod.default.getState().setToken(null);
      } catch (e) {
        console.warn(
          '[completeMicrosoftLogin] shared store non disponibile:',
          e,
        );
      }
      return {
        ok: false,
        reason: 'ROLE_NOT_ALLOWED',
        message:
          'Il tuo account è abilitato solo per l’app mobile e non può accedere al backoffice.',
      };
    }

    const isSuperConsulente = ruoli.includes('ROLE_SUPERCONSULENTE');
    const isConsulente = ruoli.includes('ROLE_CONSULENTE');
    const consulenteId =
      isSuperConsulente || isConsulente ? accountData.id : null;

    setConsulenteId(consulenteId);
    setSuperconsulente(isSuperConsulente);
    setGruppoId(accountData.gruppoId ?? null);
    return {
      ok: true,
    };
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        ok: false,
        reason: 'BACKEND_OAUTH_FAILED',
        status: err.status,
        message: err.message, // in ErrorDialog
        error: err,
      };
    }

    if (err instanceof InteractionRequiredAuthError) {
      return { ok: false, reason: 'INTERACTION_REQUIRED', error: err };
    }

    return { ok: false, reason: 'COMPLETE_LOGIN_FAILED', error: err };
  }
};
